import { useCallback, useEffect, useState } from "react";
import { MapPinned, Plus, Pencil, Trash2, Check, X, Euro } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const inputCls = "bg-[#161926] border-amber-500/20 text-slate-100";
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

function Row({ testid, view, edit, editing, onEdit, onSave, onCancel, onDelete }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2 bg-[#161926] rounded-lg p-3 border border-amber-500/10">
      <div className="flex-1 min-w-0">{editing ? edit : view}</div>
      {editing ? (
        <>
          <button data-testid={`${testid}-save`} onClick={onSave} className="text-emerald-400 p-1"><Check className="w-4 h-4" /></button>
          <button data-testid={`${testid}-cancel`} onClick={onCancel} className="text-slate-400 p-1"><X className="w-4 h-4" /></button>
        </>
      ) : (
        <>
          <button data-testid={`${testid}-edit`} onClick={onEdit} className="text-amber-300 p-1"><Pencil className="w-4 h-4" /></button>
          <button data-testid={`${testid}-delete`} onClick={onDelete} className="text-rose-400 p-1"><Trash2 className="w-4 h-4" /></button>
        </>
      )}
    </div>
  );
}

function Zones({ zones, reload }) {
  const [form, setForm] = useState({ department: "", name: "" });
  const [edit, setEdit] = useState(null);
  const add = async () => {
    try { await api.post("/admin/delivery/zones", form); toast.success("Zone ajoutée"); setForm({ department: "", name: "" }); reload(); } catch (e) { err(e); }
  };
  const save = async () => {
    try { await api.put(`/admin/delivery/zones/${edit.id}`, edit); toast.success("Zone modifiée"); setEdit(null); reload(); } catch (e) { err(e); }
  };
  const del = async (z) => {
    if (!window.confirm(`Supprimer le département ${z.department} ? Les adresses de ce département ne seront plus proposées.`)) return;
    await api.delete(`/admin/delivery/zones/${z.id}`); toast.success("Zone supprimée"); reload();
  };
  return (
    <div className="glass rounded-2xl p-5 mb-5">
      <h3 className="text-lg font-semibold text-slate-200 mb-1 flex items-center gap-2"><MapPinned className="w-4 h-4 text-amber-400" /> Zones de livraison ({zones.length})</h3>
      <p className="text-[12px] text-slate-400 mb-4">Seules les adresses de ces départements sont proposées aux clients.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <Input data-testid="zone-department-input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="N° (ex. 17)" className={`${inputCls} w-28`} />
        <Input data-testid="zone-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom (ex. Charente-Maritime)" className={`${inputCls} flex-1 min-w-[10rem]`} />
        <Button data-testid="add-zone-button" onClick={add} className="btn-gold rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      </div>
      <div className="space-y-2">
        {zones.map((z) => (
          <Row key={z.id} testid={`zone-${z.department}`} editing={edit?.id === z.id}
            view={<p className="text-sm text-slate-100"><span className="font-mono text-amber-300 font-bold mr-2">{z.department}</span>{z.name || "—"}</p>}
            edit={<div className="flex gap-2"><Input data-testid="zone-edit-department" value={edit?.department || ""} onChange={(e) => setEdit({ ...edit, department: e.target.value })} className={`${inputCls} w-20 h-8`} /><Input data-testid="zone-edit-name" value={edit?.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={`${inputCls} h-8`} /></div>}
            onEdit={() => setEdit(z)} onSave={save} onCancel={() => setEdit(null)} onDelete={() => del(z)} />
        ))}
        {zones.length === 0 && <p className="text-sm text-rose-300 text-center py-3">Aucune zone : aucune adresse ne sera proposée aux clients.</p>}
      </div>
    </div>
  );
}

function TypeSelect({ value, onChange, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} rounded-md border px-2 h-9 text-sm`}>
      <option value="postcode">Code postal</option>
      <option value="city">Ville</option>
    </select>
  );
}

function Fees({ fees, reload }) {
  const EMPTY = { type: "postcode", value: "", amount: "", driver_pay: "" };
  const [form, setForm] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const num = (v) => parseFloat(String(v ?? "").replace(",", "."));
  const payload = (f) => {
    const pay = String(f.driver_pay ?? "").trim();
    const p = num(pay);
    if (pay && (isNaN(p) || p < 0)) throw new Error("Paie livreur invalide (nombre positif ou vide pour la paie par défaut)");
    const a = num(f.amount);
    if (String(f.amount ?? "").trim() && (isNaN(a) || a < 0)) throw new Error("Frais invalides (nombre positif)");
    return { type: f.type, value: f.value, amount: a || 0, driver_pay: pay ? p : null };
  };
  const fail = (e) => (e.response ? err(e) : toast.error(e.message));
  const add = async () => {
    try { await api.post("/admin/delivery/fees", payload(form)); toast.success("Frais ajoutés"); setForm(EMPTY); reload(); } catch (e) { fail(e); }
  };
  const save = async () => {
    try { await api.put(`/admin/delivery/fees/${edit.id}`, payload(edit)); toast.success("Frais modifiés"); setEdit(null); reload(); } catch (e) { fail(e); }
  };
  const del = async (f) => {
    if (!window.confirm(`Supprimer les frais pour ${f.value} ?`)) return;
    await api.delete(`/admin/delivery/fees/${f.id}`); toast.success("Frais supprimés"); reload();
  };
  return (
    <div className="glass rounded-2xl p-5">
      <h3 className="text-lg font-semibold text-slate-200 mb-1 flex items-center gap-2"><Euro className="w-4 h-4 text-amber-400" /> Frais de livraison ({fees.length})</h3>
      <p className="text-[12px] text-slate-400 mb-4">Le code postal est vérifié en premier, puis la ville. Si l'adresse du client ne correspond à aucune ligne, la livraison est gratuite et le livreur touche la paie par défaut.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <TypeSelect testid="fee-type-select" value={form.type} onChange={(type) => setForm({ ...form, type })} />
        <Input data-testid="fee-value-input" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === "postcode" ? "Ex. 79000" : "Ex. Niort"} className={`${inputCls} flex-1 min-w-[8rem]`} />
        <Input data-testid="fee-amount-input" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Frais €" className={`${inputCls} w-24`} />
        <Input data-testid="fee-driver-pay-input" inputMode="decimal" value={form.driver_pay} onChange={(e) => setForm({ ...form, driver_pay: e.target.value })} placeholder="Paie livreur €" className={`${inputCls} w-32`} />
        <Button data-testid="add-fee-button" onClick={add} className="btn-gold rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      </div>
      <div className="space-y-2">
        {fees.map((f) => (
          <Row key={f.id} testid={`fee-${f.id}`} editing={edit?.id === f.id}
            view={<p className="text-sm text-slate-100"><span className="text-[10px] font-mono uppercase text-slate-500 mr-2">{f.type === "postcode" ? "CP" : "Ville"}</span>{f.value} <span className="font-mono text-amber-300 ml-2">{f.amount.toFixed(2)} €</span><span className="block text-[11px] text-slate-400">Paie livreur : <span className="font-mono text-emerald-300">{f.driver_pay != null ? `${f.driver_pay.toFixed(2)} €` : "par défaut"}</span></span></p>}
            edit={<div className="flex flex-wrap gap-2"><TypeSelect testid="fee-edit-type" value={edit?.type} onChange={(type) => setEdit({ ...edit, type })} /><Input data-testid="fee-edit-value" value={edit?.value || ""} onChange={(e) => setEdit({ ...edit, value: e.target.value })} className={`${inputCls} h-9 flex-1 min-w-[6rem]`} /><Input data-testid="fee-edit-amount" inputMode="decimal" value={edit?.amount ?? ""} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} className={`${inputCls} h-9 w-24`} /><Input data-testid="fee-edit-driver-pay" inputMode="decimal" placeholder="Paie €" value={edit?.driver_pay ?? ""} onChange={(e) => setEdit({ ...edit, driver_pay: e.target.value })} className={`${inputCls} h-9 w-24`} /></div>}
            onEdit={() => setEdit(f)} onSave={save} onCancel={() => setEdit(null)} onDelete={() => del(f)} />
        ))}
        {fees.length === 0 && <p className="text-sm text-slate-500 text-center py-3">Aucun frais : toutes les livraisons sont gratuites.</p>}
      </div>
    </div>
  );
}

function DefaultPay() {
  const [v, setV] = useState("");
  useEffect(() => { api.get("/admin/settings/driver-pay").then((r) => setV(String(r.data.amount))); }, []);
  const save = async () => {
    const a = parseFloat(v.replace(",", "."));
    if (isNaN(a) || a < 0) { toast.error("Montant invalide (nombre positif)"); return; }
    try { const { data } = await api.put("/admin/settings/driver-pay", { amount: a }); setV(String(data.amount)); toast.success("Paie par défaut enregistrée"); }
    catch (e) { err(e); }
  };
  return (
    <div className="glass rounded-2xl p-5 mb-5" data-testid="default-driver-pay">
      <h3 className="text-lg font-semibold text-slate-200 mb-1">Paie livreur par défaut</h3>
      <p className="text-[12px] text-slate-400 mb-3">Montant payé au livreur par commande livrée quand la ville / le code postal n'a pas de paie définie ci-dessous.</p>
      <div className="flex items-center gap-2">
        <Input data-testid="default-driver-pay-input" inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} className={`${inputCls} w-24`} />
        <span className="text-slate-300">€</span>
        <Button data-testid="default-driver-pay-save" onClick={save} className="btn-gold rounded-xl ml-2">Enregistrer</Button>
      </div>
    </div>
  );
}

export const DeliveryTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get("/admin/delivery").then((r) => setData(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <div className="fade-up max-w-2xl" data-testid="delivery-tab">
      <h2 className="text-3xl font-bold text-slate-100 mb-1">Zones et Frais de Livraison</h2>
      <p className="text-slate-400 text-sm mb-6">Choisissez les départements livrés et les frais appliqués par ville ou code postal.</p>
      <Zones zones={data.zones} reload={load} />
      <DefaultPay />
      <Fees fees={data.fees} reload={load} />
    </div>
  );
};

export const CardFeeSetting = () => {
  const [pct, setPct] = useState("");
  useEffect(() => { api.get("/admin/settings/card-fee").then((r) => setPct(String(r.data.percent))); }, []);
  const save = async () => {
    try { const { data } = await api.put("/admin/settings/card-fee", { percent: parseFloat(pct.replace(",", ".")) }); setPct(String(data.percent)); toast.success("Frais de carte enregistrés"); }
    catch (e) { err(e); }
  };
  return (
    <div className="glass rounded-2xl p-5 mb-5" data-testid="card-fee-setting">
      <h3 className="text-lg font-semibold text-slate-200 mb-1">Frais de paiement par carte</h3>
      <p className="text-[12px] text-slate-400 mb-3">Pourcentage ajouté au total (produits + livraison) quand le client paie par carte, puis total arrondi à l'euro supérieur (ex. 60 € à 6 % → 64 €). Le client voit seulement « Frais de paiement par carte ». Mettez 0 pour aucun frais.</p>
      <div className="flex items-center gap-2">
        <Input data-testid="card-fee-input" inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)} className={`${inputCls} w-24`} />
        <span className="text-slate-300">%</span>
        <Button data-testid="card-fee-save" onClick={save} className="btn-gold rounded-xl ml-2">Enregistrer</Button>
      </div>
    </div>
  );
};
